import React, {
  useRef,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Sidebar } from '@/ui/Sidebar/Sidebar';
import { Search } from '@/ui/Search/Search';
import { AssetType, FrontendSlide } from '@/types/SlideType';
import { Flex } from '@/ui/Flex/Flex';
import { T } from '@/ui/Text/Text';
import { Tooltip } from '@/ui/Tooltip/Tooltip';
import SlideLibraryAsset from '../SlideLibraryAsset';
import {
  StyledAssetsCnt,
  StyledAssetsScrollCnt,
  StyledAssetTypeChanger,
  StyledAssetTypeChangerContainer,
  StyledSearchBar,
  StyledSlideLibraryCatName,
} from './styles';
import { SlidesLibraryPlaceholder } from './SlidesLibraryPlaceholder';

interface Props {
  readonly isReplacing?: FrontendSlide | null;
  readonly onChangeNameInLibrary: (slideId: string, newName: string) => void;
  setIsSlideLibraryOpen: (value: boolean) => void;
  readonly setVideos: React.Dispatch<React.SetStateAction<FrontendSlide[]>>;
  readonly setImages: React.Dispatch<React.SetStateAction<FrontendSlide[]>>;
  readonly setWebslides: React.Dispatch<React.SetStateAction<FrontendSlide[]>>;
  readonly disallowSlideDeletion: boolean;
  readonly disableWebslides: boolean;
  readonly videos: FrontendSlide[];
  readonly images: FrontendSlide[];
  readonly webslides: FrontendSlide[];
  readonly onUpdateSlide: (
    slideId: string | null,
    slideData: Partial<FrontendSlide> | null,
    isFromLibrary?: boolean,
    slidePosition?: number,
  ) => void;
  slidePosition?: number;
}

const ITEMS_PER_PAGE = 10;

export const SlideLibrary = ({
  isReplacing,
  onChangeNameInLibrary,
  setIsSlideLibraryOpen,
  disallowSlideDeletion,
  disableWebslides = false,
  images,
  videos,
  webslides,
  setVideos,
  setImages,
  setWebslides,
  onUpdateSlide,
  slidePosition,
}: Props) => {
  const intl = useIntl();
  const hasAssets = images.length || videos.length || webslides.length;
  const [currentAssetType, setCurrentAssetType] = useState<AssetType>(
    AssetType.IMAGE,
  );
  const [searchPhrase, setSearchPhrase] = useState('');
  const [visibleItems, setVisibleItems] = useState(ITEMS_PER_PAGE);
  const searchInputId = useRef('search-input');
  const loadingRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const filterAssetsBySearchPhrase = (asset: FrontendSlide) =>
    asset.name.toUpperCase().includes(searchPhrase.toUpperCase());

  const currentView = useMemo(() => {
    const assetTypeRecord: Record<AssetType, FrontendSlide[]> = {
      [AssetType.IMAGE]: images,
      [AssetType.VIDEO]: videos,
      [AssetType.WEB]: webslides,
    };

    return (
      assetTypeRecord[currentAssetType]?.filter(filterAssetsBySearchPhrase) ||
      []
    );
  }, [searchPhrase, currentAssetType, images, videos, webslides]);

  const visibleAssets = useMemo(() => {
    return currentView.slice(0, visibleItems);
  }, [currentView, visibleItems]);

  const loadMore = useCallback(() => {
    setVisibleItems((prev) =>
      Math.min(prev + ITEMS_PER_PAGE, currentView.length),
    );
  }, [currentView.length]);

  useEffect(() => {
    setVisibleItems(ITEMS_PER_PAGE);
  }, [currentAssetType, searchPhrase]);

  useEffect(() => {
    if (!loadingRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && visibleItems < currentView.length) {
          loadMore();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(loadingRef.current);
    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loadMore, visibleItems, currentView.length]);

  const TitleMapping = {
    image: intl.formatMessage({
      defaultMessage: 'Images',
      id: 'Fip4H8',
    }),
    video: 'Videos',
    'web.url': 'Webslides',
  };

  return (
    <Sidebar
      scrollable={false}
      subtitle=""
      width="590px"
      isOpen
      onClose={() => setIsSlideLibraryOpen(false)}
      title={<FormattedMessage defaultMessage="Slide Library" id="O05e5I" />}
    >
      <StyledSearchBar direction="column" gap={8}>
        <label
          style={{
            fontWeight: '600',
            color: 'rgb(60,60,60)',
          }}
          htmlFor={searchInputId.current}
        >
          <FormattedMessage defaultMessage="Search a slide" id="pBuY5n" />:
        </label>
        <Search
          value={searchPhrase}
          setValue={setSearchPhrase}
          id={searchInputId.current}
        />
      </StyledSearchBar>

      {hasAssets ? (
        <Flex justify="center" align="center" direction="column">
          <StyledAssetTypeChangerContainer>
            {Object.entries(TitleMapping).map(([type, title]) => {
              const hideWeb = type === 'web.url' && disableWebslides;
              return (
                <StyledAssetTypeChanger
                  key={title}
                  onClick={() => {
                    if (type === 'web.url' && disableWebslides) return;
                    setCurrentAssetType(type as AssetType);
                  }}
                  checked={currentAssetType === type}
                  disabled={hideWeb}
                >
                  {hideWeb && (
                    <Tooltip
                      title={
                        <FormattedMessage
                          defaultMessage="Not for splitscreen"
                          id="RDpLWx"
                        />
                      }
                    >
                      {title}
                    </Tooltip>
                  )}
                  {!hideWeb && title}
                </StyledAssetTypeChanger>
              );
            })}
          </StyledAssetTypeChangerContainer>
          {currentView.length ? (
            <div
              style={{
                height: '70vh',
                transition: 'height .2s linear',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
              key={currentAssetType}
            >
              <StyledSlideLibraryCatName h3>
                {TitleMapping[currentAssetType]}
              </StyledSlideLibraryCatName>
              <StyledAssetsCnt>
                <StyledAssetsScrollCnt className="library-item-container">
                  {visibleAssets.map((asset) => (
                    <SlideLibraryAsset
                      isReplacing={isReplacing}
                      setIsSlideLibraryOpen={setIsSlideLibraryOpen}
                      onChangeNameInLibrary={onChangeNameInLibrary}
                      key={asset.id}
                      disallowSlideDeletion={disallowSlideDeletion}
                      setVideos={setVideos}
                      setImages={setImages}
                      setWebslides={setWebslides}
                      asset={asset}
                      onUpdateSlide={onUpdateSlide}
                      slidePosition={slidePosition}
                    />
                  ))}
                  {visibleItems < currentView.length && (
                    <div ref={loadingRef} style={{ height: '20px' }} />
                  )}
                </StyledAssetsScrollCnt>
              </StyledAssetsCnt>
            </div>
          ) : (
            <div style={{ padding: '32px' }}>
              <T>
                <FormattedMessage
                  defaultMessage="no slides with this name"
                  id="OQCMJW"
                />
              </T>
            </div>
          )}
        </Flex>
      ) : (
        <SlidesLibraryPlaceholder />
      )}
    </Sidebar>
  );
};
